import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { gastosAPI, proveedoresAPI } from '../services/api';
import { getImageUrl } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const Gastos = () => {
  const { formatCurrency } = useApp();
  const { usuario, esAdmin, esAdminOGerente, modoEspectador } = useAuth();

  // Tab activo
  const [tab, setTab] = useState('gastos');

  // ─── Estado: Gastos ───
  const [gastos, setGastos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loadingGastos, setLoadingGastos] = useState(true);
  const [filtros, setFiltros] = useState({ categoria_id: '', estado: '', fecha_desde: '', fecha_hasta: '' });

  // ─── Estado: Categorías ───
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);

  // ─── Estado: Caja Chica ───
  const [cajaChica, setCajaChica] = useState(null);
  const [ultimaCerrada, setUltimaCerrada] = useState(null);
  const [loadingCC, setLoadingCC] = useState(true);

  // ─── Modales ───
  const [showNuevoGasto, setShowNuevoGasto] = useState(false);
  const [showEditCategoria, setShowEditCategoria] = useState(false);
  const [showAbrirCC, setShowAbrirCC] = useState(false);
  const [showReponerCC, setShowReponerCC] = useState(false);
  const [showAnular, setShowAnular] = useState(null);
  const [saving, setSaving] = useState(false);

  // ─── Formularios ───
  const [gastoForm, setGastoForm] = useState({
    categoria_id: '', proveedor_id: '', concepto: '', descripcion: '',
    monto: '', metodo_pago: 'caja_chica', numero_documento: '', notas: '',
    aplica_iva: false, pagar_directo: false
  });
  const [catForm, setCatForm] = useState({ id: null, nombre: '', descripcion: '', icono: 'bi-tag', color: '#6c757d' });
  const [ccForm, setCcForm] = useState({ monto_asignado: '', observaciones: '' });
  const [reponerForm, setReponerForm] = useState({ monto: '' });
  const [anularMotivo, setAnularMotivo] = useState('');
  const [editGastoId, setEditGastoId] = useState(null);

  // ─── Paginación Gastos ───
  const [paginaActual, setPaginaActual] = useState(1);
  const gastosPorPagina = 20;

  // ─── Estado: Comprobantes ───
  const [showDetalleGasto, setShowDetalleGasto] = useState(null);
  const [comprobantes, setComprobantes] = useState([]);
  const [uploadingComp, setUploadingComp] = useState(false);
  const fileInputRef = useRef(null);

  // ─── Adjuntos en modal de creación (hasta 6 archivos) ───
  const [adjuntosGasto, setAdjuntosGasto] = useState([]);
  const adjuntosGastoRef = useRef(null);

  // ─── Cargar datos ───
  const cargarGastos = useCallback(async () => {
    setLoadingGastos(true);
    try {
      const params = { limit: 1000 };
      if (filtros.categoria_id) params.categoria_id = filtros.categoria_id;
      if (filtros.estado) params.estado = filtros.estado;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;

      const [gastosRes, resumenRes] = await Promise.all([
        gastosAPI.getAll(params),
        gastosAPI.getResumen()
      ]);

      // FIX: null checks on API response data arrays
      if (gastosRes.data?.success) setGastos(gastosRes.data.data || []);
      if (resumenRes.data?.success) setResumen(resumenRes.data.data || null);
    } catch (e) { console.error(e); toast.error('Error al cargar gastos'); }
    finally { setLoadingGastos(false); }
  }, [filtros]);

  const cargarCategorias = useCallback(async () => {
    try {
      const res = await gastosAPI.getCategorias();
      // FIX: null check on categorias data
      if (res.data?.success) setCategorias(res.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const cargarProveedores = useCallback(async () => {
    try {
      const res = await proveedoresAPI.getAll({ limit: 1000 });
      // FIX: null check on proveedores data
      if (res.data?.success) setProveedores(res.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const cargarCajaChica = useCallback(async () => {
    setLoadingCC(true);
    try {
      const res = await gastosAPI.getCajaChica();
      if (res.data?.success) {
        setCajaChica(res.data.data || null);
        setUltimaCerrada(res.data.ultima_cerrada || null);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingCC(false); }
  }, []);

  useEffect(() => {
    cargarGastos();
    cargarCategorias();
    cargarProveedores();
    cargarCajaChica();
  }, [cargarGastos, cargarCategorias, cargarProveedores, cargarCajaChica]);

  // Resetear página al cambiar filtros
  useEffect(() => { setPaginaActual(1); }, [filtros]);

  // ─── Paginación calculada ───
  const totalPaginas = Math.ceil(gastos.length / gastosPorPagina);
  const gastosEnPagina = gastos.slice((paginaActual - 1) * gastosPorPagina, paginaActual * gastosPorPagina);

  // ─── Handlers: Adjuntos al crear gasto ───
  const handleAdjuntosGastoChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.type.startsWith('image/') || f.type === 'application/pdf');
    const invalidCount = files.length - validFiles.length;
    if (invalidCount > 0) toast.warning(`${invalidCount} archivo(s) ignorados (solo PDF e imágenes)`);
    setAdjuntosGasto(prev => {
      const combined = [...prev, ...validFiles];
      if (combined.length > 6) { toast.warning('Máximo 6 adjuntos'); return combined.slice(0, 6); }
      return combined;
    });
    if (adjuntosGastoRef.current) adjuntosGastoRef.current.value = '';
  };
  const quitarAdjuntoGasto = (idx) => setAdjuntosGasto(prev => prev.filter((_, i) => i !== idx));

  // ─── Handlers: Gastos ───
  const handleCrearGasto = async () => {
    if (!gastoForm.concepto || !gastoForm.monto) {
      toast.error('Concepto y monto son requeridos');
      return;
    }
    if (!editGastoId && adjuntosGasto.length === 0) {
      toast.error('Debe adjuntar al menos un comprobante (foto o PDF)');
      return;
    }
    setSaving(true);
    try {
      if (editGastoId) {
        const res = await gastosAPI.update(editGastoId, gastoForm);
        // FIX: null check on API response
        if (res.data?.success) {
          toast.success('Gasto actualizado');
          setShowNuevoGasto(false);
          setEditGastoId(null);
          cargarGastos();
        }
      } else {
        const res = await gastosAPI.create(gastoForm);
        // FIX: null check on API response
        if (res.data?.success) {
          const gastoCreado = res.data.data;
          // Subir adjuntos si hay
          if (adjuntosGasto.length > 0 && gastoCreado?.id) {
            try {
              await gastosAPI.uploadComprobantes(gastoCreado.id, adjuntosGasto);
              toast.success(`Gasto registrado con ${adjuntosGasto.length} comprobante(s)`);
            } catch (uploadErr) {
              toast.warning('Gasto creado pero error al subir comprobantes. Adjúntelos desde el detalle.');
              console.error('Error subiendo comprobantes:', uploadErr);
            }
          } else {
            toast.success(gastoForm.pagar_directo ? 'Gasto registrado y pagado' : 'Gasto registrado');
          }
          setShowNuevoGasto(false);
          setAdjuntosGasto([]);
          cargarGastos();
          cargarCajaChica();
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar gasto');
    } finally { setSaving(false); }
  };

  const handleAprobar = async (id) => {
    try {
      const res = await gastosAPI.aprobar(id);
      // FIX: null check on API response
      if (res.data?.success) { toast.success('Gasto aprobado'); cargarGastos(); }
    } catch (e) { toast.error(e.response?.data?.error || 'Error al aprobar'); }
  };

  const handlePagar = async (id) => {
    try {
      const res = await gastosAPI.pagar(id);
      // FIX: null check on API response
      if (res.data?.success) { toast.success('Gasto pagado'); cargarGastos(); cargarCajaChica(); }
    } catch (e) { toast.error(e.response?.data?.error || 'Error al pagar'); }
  };

  const handleAnular = async () => {
    if (!showAnular) return;
    setSaving(true);
    try {
      const res = await gastosAPI.anular(showAnular, { motivo: anularMotivo });
      // FIX: null check on API response
      if (res.data?.success) {
        toast.success('Gasto anulado');
        setShowAnular(null);
        setAnularMotivo('');
        cargarGastos();
        cargarCajaChica();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al anular');
    } finally { setSaving(false); }
  };

  const abrirEditarGasto = (gasto) => {
    setEditGastoId(gasto.id);
    setGastoForm({
      categoria_id: gasto.categoria_id || '', proveedor_id: gasto.proveedor_id || '',
      concepto: gasto.concepto, descripcion: gasto.descripcion || '',
      monto: gasto.monto, metodo_pago: gasto.metodo_pago || 'efectivo',
      numero_documento: gasto.numero_documento || '', notas: gasto.notas || '',
      aplica_iva: gasto.aplica_iva || false,
      pagar_directo: false
    });
    setShowNuevoGasto(true);
  };

  // ─── Handlers: Detalle Gasto + Comprobantes ───
  const verDetalleGasto = async (gasto) => {
    setShowDetalleGasto(gasto);
    try {
      const res = await gastosAPI.getComprobantes(gasto.id);
      // FIX: null check on comprobantes data array
      if (res.data?.success) setComprobantes(res.data.data || []);
      else setComprobantes([]);
    } catch { setComprobantes([]); }
  };

  const handleUploadComprobantes = async (files) => {
    if (!showDetalleGasto) return;
    // FIX: validate files before uploading
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    setUploadingComp(true);
    try {
      const res = await gastosAPI.uploadComprobantes(showDetalleGasto.id, fileArray);
      if (res.data?.success) {
        toast.success('Comprobante(s) subido(s)');
        // Recargar comprobantes
        const cRes = await gastosAPI.getComprobantes(showDetalleGasto.id);
        // FIX: null check on reload comprobantes data
        if (cRes.data?.success) setComprobantes(cRes.data.data || []);
        cargarGastos();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al subir comprobante');
    } finally { setUploadingComp(false); }
  };

  const handleDeleteComprobante = async (comprobanteId) => {
    if (!showDetalleGasto) return;
    if (!window.confirm('¿Eliminar este comprobante?')) return;
    try {
      await gastosAPI.deleteComprobante(showDetalleGasto.id, comprobanteId);
      toast.success('Comprobante eliminado');
      const cRes = await gastosAPI.getComprobantes(showDetalleGasto.id);
      // FIX: null check on comprobantes reload after delete
      if (cRes.data?.success) setComprobantes(cRes.data.data || []);
      cargarGastos();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al eliminar');
    }
  };

  // ─── Handlers: Categorías ───
  const handleGuardarCategoria = async () => {
    if (!catForm.nombre) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      if (catForm.id) {
        await gastosAPI.updateCategoria(catForm.id, catForm);
        toast.success('Categoría actualizada');
      } else {
        await gastosAPI.createCategoria(catForm);
        toast.success('Categoría creada');
      }
      setShowEditCategoria(false);
      cargarCategorias();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar categoría');
    } finally { setSaving(false); }
  };

  const toggleCategoriaActiva = async (cat) => {
    try {
      await gastosAPI.updateCategoria(cat.id, { activo: !cat.activo });
      cargarCategorias();
    } catch (e) { toast.error('Error al cambiar estado'); }
  };

  // ─── Handlers: Caja Chica ───
  const handleAbrirCC = async () => {
    if (!ccForm.monto_asignado || parseFloat(ccForm.monto_asignado) <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    setSaving(true);
    try {
      const res = await gastosAPI.abrirCajaChica(ccForm);
      // FIX: null check on API response
      if (res.data?.success) {
        toast.success('Caja chica abierta');
        setShowAbrirCC(false);
        cargarCajaChica();
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleReponerCC = async () => {
    if (!reponerForm.monto || parseFloat(reponerForm.monto) <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    setSaving(true);
    try {
      const res = await gastosAPI.reponerCajaChica(reponerForm);
      // FIX: null check on API response
      if (res.data?.success) {
        toast.success('Caja chica repuesta');
        setShowReponerCC(false);
        cargarCajaChica();
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleCerrarCC = async () => {
    if (!window.confirm('¿Está seguro de cerrar la caja chica?')) return;
    try {
      const res = await gastosAPI.cerrarCajaChica({});
      // FIX: null check on API response
      if (res.data?.success) {
        toast.success('Caja chica cerrada');
        cargarCajaChica();
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const handleReabrirCC = async () => {
    if (!window.confirm(`¿Reabrir caja chica con saldo de ${formatCurrency(ultimaCerrada?.monto_disponible)}?`)) return;
    try {
      const res = await gastosAPI.reabrirCajaChica();
      if (res.data?.success) {
        toast.success(res.data.message || 'Caja chica reabierta');
        cargarCajaChica();
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  // ─── Helpers ───
  const estadoBadge = (estado) => {
    const map = {
      pendiente: 'bg-warning text-dark',
      aprobado: 'bg-info',
      pagado: 'bg-success',
      anulado: 'bg-danger'
    };
    return map[estado] || 'bg-secondary';
  };

  // FIX: guard against NaN/division-by-zero in porcentaje calculation
  const porcentajeGastado = cajaChica && parseFloat(cajaChica.monto_asignado) > 0
    ? Math.min(100, ((parseFloat(cajaChica.monto_gastado || 0) / parseFloat(cajaChica.monto_asignado)) * 100))
    : 0;

  const barColor = porcentajeGastado > 80 ? '#dc3545' : porcentajeGastado > 60 ? '#fd7e14' : '#198754';

  // ================================================================
  // RENDER
  // ================================================================
  return (
    <div>
      {/* ─── TABS ─── */}
      <ul className="nav nav-pills mb-4">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'gastos' ? 'active' : ''}`} onClick={() => setTab('gastos')}>
            <i className="bi bi-receipt me-1"></i>Gastos
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'caja-chica' ? 'active' : ''}`} onClick={() => { setTab('caja-chica'); cargarCajaChica(); }}>
            <i className="bi bi-safe me-1"></i>Caja Chica
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'categorias' ? 'active' : ''}`} onClick={() => setTab('categorias')}>
            <i className="bi bi-tags me-1"></i>Categorías
          </button>
        </li>
      </ul>

      {/* ================================================================ */}
      {/* TAB: GASTOS                                                      */}
      {/* ================================================================ */}
      {tab === 'gastos' && (
        <>
          {/* Cards resumen */}
          {resumen && (
            <div className="row g-3 mb-3">
              <div className="col-6 col-xl-3">
                <div className="stat-card">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="stat-label mb-1">Total del Mes</p>
                      <h3 className="stat-value mb-0">{formatCurrency(resumen.totales?.total_pagado || 0)}</h3>
                    </div>
                    <div className="stat-icon bg-danger bg-opacity-10 text-danger"><i className="bi bi-cash-stack"></i></div>
                  </div>
                  <small className="text-muted mt-2 d-block">{resumen.totales?.cant_pagados || 0} pagados</small>
                </div>
              </div>
              <div className="col-6 col-xl-3">
                <div className="stat-card">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="stat-label mb-1">Pendientes</p>
                      <h3 className="stat-value mb-0 text-warning">{formatCurrency(parseFloat(resumen.totales?.total_pendiente || 0) + parseFloat(resumen.totales?.total_aprobado || 0))}</h3>
                    </div>
                    <div className="stat-icon bg-warning bg-opacity-10 text-warning"><i className="bi bi-hourglass-split"></i></div>
                  </div>
                  <small className="text-muted mt-2 d-block">{(parseInt(resumen.totales?.cant_pendientes || 0) + parseInt(resumen.totales?.cant_aprobados || 0))} por pagar</small>
                </div>
              </div>
              <div className="col-6 col-xl-3">
                <div className="stat-card">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="stat-label mb-1">Total Gastos</p>
                      <h3 className="stat-value mb-0">{resumen.totales?.total_gastos || 0}</h3>
                    </div>
                    <div className="stat-icon bg-primary bg-opacity-10 text-primary"><i className="bi bi-receipt"></i></div>
                  </div>
                  <small className="text-muted mt-2 d-block">este mes</small>
                </div>
              </div>
              <div className="col-6 col-xl-3">
                <div className="stat-card">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="stat-label mb-1">Caja Chica</p>
                      <h3 className="stat-value mb-0 text-success">{formatCurrency(resumen.caja_chica?.monto_disponible || 0)}</h3>
                    </div>
                    <div className="stat-icon bg-success bg-opacity-10 text-success"><i className="bi bi-safe"></i></div>
                  </div>
                  <small className="text-muted mt-2 d-block">disponible</small>
                </div>
              </div>
            </div>
          )}

          {/* Filtros + Botón */}
          <div className="card mb-3">
            <div className="card-body py-2">
              <div className="row g-2 align-items-center">
                <div className="col-auto">
                  <select className="form-select form-select-sm" value={filtros.categoria_id}
                    onChange={(e) => setFiltros({ ...filtros, categoria_id: e.target.value })}>
                    <option value="">Todas las categorías</option>
                    {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div className="col-auto">
                  <select className="form-select form-select-sm" value={filtros.estado}
                    onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}>
                    <option value="">Todos los estados</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="aprobado">Aprobado</option>
                    <option value="pagado">Pagado</option>
                    <option value="anulado">Anulado</option>
                  </select>
                </div>
                <div className="col-auto">
                  <input type="date" className="form-control form-control-sm" value={filtros.fecha_desde}
                    onChange={(e) => setFiltros({ ...filtros, fecha_desde: e.target.value })} />
                </div>
                <div className="col-auto">
                  <input type="date" className="form-control form-control-sm" value={filtros.fecha_hasta}
                    onChange={(e) => setFiltros({ ...filtros, fecha_hasta: e.target.value })} />
                </div>
                <div className="col-auto ms-auto">
                  <button className="btn btn-primary btn-sm" disabled={modoEspectador} onClick={() => {
                    setEditGastoId(null);
                    setGastoForm({
                      categoria_id: '', proveedor_id: '', concepto: '', descripcion: '',
                      monto: '', metodo_pago: 'caja_chica', numero_documento: '', notas: '',
                      aplica_iva: false, pagar_directo: false
                    });
                    setAdjuntosGasto([]);
                    setShowNuevoGasto(true);
                  }}>
                    <i className="bi bi-plus-lg me-1"></i>Nuevo Gasto
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de gastos */}
          <div className="card">
            <div className="card-body p-0">
              {loadingGastos ? (
                <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
              ) : gastos.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-receipt fs-1 d-block mb-2"></i>
                  <p>No hay gastos registrados</p>
                </div>
              ) : (
                <>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Código</th>
                        <th>Categoría</th>
                        <th>Concepto</th>
                        <th>Proveedor</th>
                        <th className="text-end">Monto</th>
                        <th>Estado</th>
                        <th>Control</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gastosEnPagina.map(g => (
                        <tr key={g.id} className={g.estado === 'anulado' ? 'text-decoration-line-through opacity-50' : ''}>
                          {/* FIX: null check for created_at date */}
                          <td className="text-muted small">{g.created_at ? new Date(g.created_at).toLocaleDateString('es-EC') : '—'}</td>
                          <td><span className="badge bg-primary bg-opacity-10 text-primary">{g.codigo}</span></td>
                          <td>
                            {g.categoria_nombre ? (
                              <span className="badge" style={{ backgroundColor: g.categoria_color + '22', color: g.categoria_color }}>
                                <i className={`bi ${g.categoria_icono} me-1`}></i>{g.categoria_nombre}
                              </span>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td>{g.concepto}</td>
                          <td className="text-muted small">{g.proveedor_nombre || '—'}</td>
                          <td className="text-end">
                            <span className="fw-bold text-danger">{formatCurrency(parseFloat(g.total_con_iva) > 0 ? g.total_con_iva : g.monto)}</span>
                            {g.aplica_iva && (
                              <div className="small text-muted">
                                Base: {formatCurrency(g.monto)} + IVA: {formatCurrency(g.monto_iva)}
                              </div>
                            )}
                          </td>
                          <td><span className={`badge ${estadoBadge(g.estado)}`}>{g.estado}</span></td>
                          <td>
                            {(() => {
                              const tieneComp = g.tiene_comprobante_real || g.tiene_comprobante;
                              if (!tieneComp && g.estado !== 'anulado') return (
                                <span className="badge bg-danger bg-opacity-10 text-danger" title="Sin comprobante">
                                  <i className="bi bi-exclamation-triangle me-1"></i>Sin comprobante
                                </span>
                              );
                              if (tieneComp) return (
                                <span className="badge bg-success bg-opacity-10 text-success">
                                  <i className="bi bi-paperclip me-1"></i>Comprobante ✓
                                </span>
                              );
                              return <span className="text-muted small">—</span>;
                            })()}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-outline-primary" title="Ver detalle" onClick={() => verDetalleGasto(g)}>
                                <i className="bi bi-eye"></i>
                              </button>
                              {g.estado === 'pendiente' && (
                                <>
                                  <button className="btn btn-sm btn-outline-info" title="Aprobar" onClick={() => handleAprobar(g.id)}>
                                    <i className="bi bi-check-lg"></i>
                                  </button>
                                  <button className="btn btn-sm btn-outline-success" title="Pagar" onClick={() => handlePagar(g.id)}>
                                    <i className="bi bi-cash"></i>
                                  </button>
                                  <button className="btn btn-sm btn-outline-secondary" title="Editar" onClick={() => abrirEditarGasto(g)}>
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                </>
                              )}
                              {g.estado === 'aprobado' && (
                                <button className="btn btn-sm btn-outline-success" title="Pagar" onClick={() => handlePagar(g.id)}>
                                  <i className="bi bi-cash"></i>
                                </button>
                              )}
                              {['pendiente', 'aprobado', 'pagado'].includes(g.estado) && (
                                <button className="btn btn-sm btn-outline-danger" title="Anular"
                                  onClick={() => { setShowAnular(g.id); setAnularMotivo(''); }}>
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginación */}
                {totalPaginas > 1 && (
                  <div className="d-flex justify-content-between align-items-center p-3">
                    <small className="text-muted">
                      Mostrando {(paginaActual - 1) * gastosPorPagina + 1}–{Math.min(paginaActual * gastosPorPagina, gastos.length)} de {gastos.length} gastos
                    </small>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setPaginaActual(1)} disabled={paginaActual === 1}>
                            <i className="bi bi-chevron-double-left"></i>
                          </button>
                        </li>
                        <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setPaginaActual(p => p - 1)} disabled={paginaActual === 1}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === totalPaginas || Math.abs(p - paginaActual) <= 1)
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, idx) =>
                            p === '...' ? (
                              <li key={`dots-${idx}`} className="page-item disabled"><span className="page-link">…</span></li>
                            ) : (
                              <li key={p} className={`page-item ${paginaActual === p ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setPaginaActual(p)}>{p}</button>
                              </li>
                            )
                          )}
                        <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setPaginaActual(p => p + 1)} disabled={paginaActual === totalPaginas}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                        <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual === totalPaginas}>
                            <i className="bi bi-chevron-double-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* TAB: CAJA CHICA                                                  */}
      {/* ================================================================ */}
      {tab === 'caja-chica' && (
        <>
          {loadingCC ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : !cajaChica ? (
            <div className="text-center py-5">
              <i className="bi bi-safe fs-1 d-block mb-3 text-muted"></i>
              <h5 className="text-muted">No hay caja chica activa</h5>
              {ultimaCerrada ? (
                <>
                  <div className="card mx-auto mb-4" style={{ maxWidth: '500px' }}>
                    <div className="card-body">
                      <h6 className="card-title mb-3"><i className="bi bi-clock-history me-2"></i>Última Caja Chica</h6>
                      <div className="row text-start">
                        <div className="col-4">
                          <small className="text-muted">Asignado</small>
                          <p className="fw-bold mb-1">{formatCurrency(ultimaCerrada.monto_asignado)}</p>
                        </div>
                        <div className="col-4">
                          <small className="text-muted">Gastado</small>
                          <p className="fw-bold text-danger mb-1">{formatCurrency(ultimaCerrada.monto_gastado)}</p>
                        </div>
                        <div className="col-4">
                          <small className="text-muted">Saldo restante</small>
                          <p className="fw-bold text-success mb-1">{formatCurrency(ultimaCerrada.monto_disponible)}</p>
                        </div>
                      </div>
                      <small className="text-muted">
                        Cerrada: {ultimaCerrada.fecha_cierre ? new Date(ultimaCerrada.fecha_cierre).toLocaleString('es-EC') : '—'}
                        {ultimaCerrada.cerrada_por_nombre && ` por ${ultimaCerrada.cerrada_por_nombre}`}
                      </small>
                    </div>
                  </div>
                  <div className="d-flex justify-content-center gap-3">
                    <button className="btn btn-success" onClick={handleReabrirCC}>
                      <i className="bi bi-unlock me-2"></i>Reabrir Caja Chica ({formatCurrency(ultimaCerrada.monto_disponible)})
                    </button>
                    <button className="btn btn-outline-primary" onClick={() => {
                      setCcForm({ monto_asignado: '', observaciones: '' });
                      setShowAbrirCC(true);
                    }}>
                      <i className="bi bi-plus-lg me-2"></i>Abrir Nueva
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-muted mb-4">Abra una caja chica para controlar los gastos operativos</p>
                  <button className="btn btn-primary" onClick={() => {
                    setCcForm({ monto_asignado: '', observaciones: '' });
                    setShowAbrirCC(true);
                  }}>
                    <i className="bi bi-plus-lg me-2"></i>Abrir Caja Chica
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Card resumen caja chica */}
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="stat-card">
                    <p className="stat-label mb-1">Monto Asignado</p>
                    <h3 className="stat-value mb-0">{formatCurrency(cajaChica.monto_asignado)}</h3>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="stat-card">
                    <p className="stat-label mb-1">Gastado</p>
                    <h3 className="stat-value mb-0 text-danger">{formatCurrency(cajaChica.monto_gastado)}</h3>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="stat-card">
                    <p className="stat-label mb-1">Disponible</p>
                    <h3 className="stat-value mb-0 text-success">{formatCurrency(cajaChica.monto_disponible)}</h3>
                  </div>
                </div>
              </div>

              {/* Barra de progreso */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-semibold">Uso del fondo</span>
                    {/* FIX: null safety — toFixed on possibly NaN */}
                    <span className="fw-bold" style={{ color: barColor }}>{(parseFloat(porcentajeGastado) || 0).toFixed(1)}%</span>
                  </div>
                  <div className="progress" style={{ height: '12px' }}>
                    <div className="progress-bar" style={{ width: `${porcentajeGastado}%`, backgroundColor: barColor }}></div>
                  </div>
                  <div className="d-flex justify-content-between mt-3">
                    {porcentajeGastado >= 60 && (
                      <button className="btn btn-warning btn-sm" onClick={() => {
                        setReponerForm({ monto: '' });
                        setShowReponerCC(true);
                      }}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Reponer Fondo
                      </button>
                    )}
                    <button className="btn btn-outline-danger btn-sm ms-auto" onClick={handleCerrarCC}>
                      <i className="bi bi-lock me-1"></i>Cerrar Caja Chica
                    </button>
                  </div>
                </div>
              </div>

              {/* Info responsable */}
              <div className="card mb-4">
                <div className="card-body py-2">
                  <div className="row text-muted small">
                    <div className="col-auto"><i className="bi bi-person me-1"></i>Responsable: <strong>{cajaChica.responsable_nombre}</strong></div>
                    <div className="col-auto"><i className="bi bi-calendar me-1"></i>Abierta: <strong>{cajaChica.fecha_apertura ? new Date(cajaChica.fecha_apertura).toLocaleDateString('es-EC') : '—'}</strong></div>
                    {cajaChica.abierta_por_nombre && (
                      <div className="col-auto">
                        <small className="text-muted">
                          <i className="bi bi-person me-1"></i>Abierto por: {cajaChica.abierta_por_nombre}
                        </small>
                      </div>
                    )}
                    {cajaChica.cerrada_por_nombre && (
                      <div className="col-auto">
                        <small className="text-muted">
                          <i className="bi bi-person-lock me-1"></i>Cerrado por: {cajaChica.cerrada_por_nombre}
                        </small>
                      </div>
                    )}
                    {cajaChica.repuesto_por_nombre && (
                      <div className="col-auto">
                        <small className="text-muted">
                          <i className="bi bi-person-up me-1"></i>Repuesto por: {cajaChica.repuesto_por_nombre}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Movimientos recientes */}
              <div className="card">
                <div className="card-header"><h6 className="mb-0"><i className="bi bi-clock-history me-2"></i>Movimientos de Caja Chica</h6></div>
                <div className="card-body p-0">
                  {cajaChica.movimientos?.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead><tr><th>Fecha</th><th>Tipo</th><th>Concepto</th><th className="text-end">Monto</th><th>Usuario</th></tr></thead>
                        <tbody>
                          {(cajaChica.movimientos || []).map(m => (
                            <tr key={m.id}>
                              {/* FIX: null check for movimiento date */}
                              <td className="text-muted small">{m.created_at ? new Date(m.created_at).toLocaleString('es-EC', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                              <td>
                                <span className={`badge ${m.tipo === 'gasto' ? 'bg-danger' : m.tipo === 'reposicion' ? 'bg-success' : m.tipo === 'asignacion' ? 'bg-primary' : 'bg-info'}`}>
                                  {m.tipo}
                                </span>
                              </td>
                              <td>{m.concepto}</td>
                              <td className={`text-end fw-bold ${m.tipo === 'gasto' ? 'text-danger' : 'text-success'}`}>
                                {m.tipo === 'gasto' ? '-' : '+'}{formatCurrency(m.monto)}
                              </td>
                              <td className="text-muted small">{m.usuario_nombre}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted">Sin movimientos aún</div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ================================================================ */}
      {/* TAB: CATEGORÍAS                                                  */}
      {/* ================================================================ */}
      {tab === 'categorias' && (
        <>
          <div className="d-flex justify-content-between mb-3">
            <h5 className="mb-0">Categorías de Gastos</h5>
            <button className="btn btn-primary btn-sm" onClick={() => {
              setCatForm({ id: null, nombre: '', descripcion: '', icono: 'bi-tag', color: '#6c757d' });
              setShowEditCategoria(true);
            }}>
              <i className="bi bi-plus-lg me-1"></i>Nueva Categoría
            </button>
          </div>

          <div className="row g-3">
            {categorias.map(cat => (
              <div key={cat.id} className="col-md-6 col-lg-4">
                <div className="card h-100">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: 48, height: 48, backgroundColor: cat.color + '22', color: cat.color, fontSize: '1.3rem' }}>
                      <i className={`bi ${cat.icono}`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-0">{cat.nombre}</h6>
                      {cat.descripcion && <small className="text-muted">{cat.descripcion}</small>}
                    </div>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => {
                        setCatForm({ id: cat.id, nombre: cat.nombre, descripcion: cat.descripcion || '', icono: cat.icono, color: cat.color });
                        setShowEditCategoria(true);
                      }}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className={`btn btn-sm ${cat.activo ? 'btn-outline-danger' : 'btn-outline-success'}`}
                        onClick={() => toggleCategoriaActiva(cat)} title={cat.activo ? 'Desactivar' : 'Activar'}>
                        <i className={`bi ${cat.activo ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* MODAL: Nuevo/Editar Gasto                                        */}
      {/* ================================================================ */}
      {showNuevoGasto && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowNuevoGasto(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-receipt me-2"></i>{editGastoId ? 'Editar Gasto' : 'Nuevo Gasto'}
                  </h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowNuevoGasto(false)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Categoría</label>
                      <select className="form-select" value={gastoForm.categoria_id}
                        onChange={(e) => setGastoForm({ ...gastoForm, categoria_id: e.target.value })}>
                        <option value="">Seleccionar...</option>
                        {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Proveedor (opcional)</label>
                      <select className="form-select" value={gastoForm.proveedor_id}
                        onChange={(e) => setGastoForm({ ...gastoForm, proveedor_id: e.target.value })}>
                        <option value="">Sin proveedor</option>
                        {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Concepto *</label>
                      <input type="text" className="form-control" placeholder="Ej: Pago servicio de agua potable"
                        value={gastoForm.concepto}
                        onChange={(e) => setGastoForm({ ...gastoForm, concepto: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-semibold">Monto *</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input type="number" className="form-control" step="0.01" min="0.01"
                          value={gastoForm.monto}
                          onChange={(e) => setGastoForm({ ...gastoForm, monto: e.target.value })} />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small fw-semibold">IVA</label>
                      <select className="form-select" value={gastoForm.aplica_iva ? 'si' : 'no'}
                        onChange={(e) => setGastoForm({ ...gastoForm, aplica_iva: e.target.value === 'si' })}>
                        <option value="no">Sin IVA</option>
                        <option value="si">Con IVA 15%</option>
                      </select>
                      {/* FIX: IVA preview with NaN guard for empty/invalid monto */}
                      {gastoForm.aplica_iva && gastoForm.monto && !isNaN(parseFloat(gastoForm.monto)) && (
                        <small className="text-muted">Total: ${((parseFloat(gastoForm.monto) || 0) * 1.15).toFixed(2)}</small>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-semibold">Método de Pago</label>
                      <select className="form-select" value={gastoForm.metodo_pago}
                        onChange={(e) => setGastoForm({ ...gastoForm, metodo_pago: e.target.value, pagar_directo: e.target.value === 'caja_chica' ? false : gastoForm.pagar_directo })}>
                        <option value="caja_chica">Caja Chica</option>
                        <option value="efectivo">Efectivo</option>
                        <option value="transferencia">Transferencia</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold"># Documento / Factura</label>
                      <input type="text" className="form-control" placeholder="Opcional"
                        value={gastoForm.numero_documento}
                        onChange={(e) => setGastoForm({ ...gastoForm, numero_documento: e.target.value })} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Descripción / Notas</label>
                      <textarea className="form-control" rows="2" placeholder="Detalles adicionales..."
                        value={gastoForm.notas}
                        onChange={(e) => setGastoForm({ ...gastoForm, notas: e.target.value })} />
                    </div>
                    {/* ─── Adjuntos / Comprobantes ─── */}
                    {!editGastoId && (
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
                            <span className="small fw-semibold"><i className="bi bi-paperclip me-1"></i>Comprobantes</span>
                            <span className="badge bg-secondary">{adjuntosGasto.length} / 6</span>
                          </div>
                          <div className="card-body py-2">
                            <input ref={adjuntosGastoRef} type="file" className="d-none" multiple
                              accept="image/*,application/pdf"
                              onChange={handleAdjuntosGastoChange} />
                            {adjuntosGasto.length === 0 ? (
                              <div className="border rounded p-3 text-center" style={{ borderStyle: 'dashed', cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                onClick={() => adjuntosGastoRef.current?.click()}>
                                <i className="bi bi-cloud-arrow-up fs-4 text-muted d-block mb-1"></i>
                                <small className="text-muted">Clic para adjuntar facturas o comprobantes (PDF, imágenes, máx. 6)</small>
                              </div>
                            ) : (
                              <>
                                {adjuntosGasto.map((file, idx) => (
                                  <div key={idx} className="d-flex align-items-center gap-2 border rounded py-1 px-2 mb-1">
                                    {file.type === 'application/pdf' ? (
                                      <i className="bi bi-file-pdf text-danger"></i>
                                    ) : (
                                      <img src={URL.createObjectURL(file)} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />
                                    )}
                                    <span className="small text-truncate flex-grow-1">{file.name}</span>
                                    <span className="text-muted" style={{fontSize:'0.7rem'}}>{(file.size/1024).toFixed(0)}KB</span>
                                    <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => quitarAdjuntoGasto(idx)}><i className="bi bi-x"></i></button>
                                  </div>
                                ))}
                                {adjuntosGasto.length < 6 && (
                                  <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={() => adjuntosGastoRef.current?.click()}>
                                    <i className="bi bi-plus me-1"></i>Agregar más
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {gastoForm.metodo_pago === 'caja_chica' && cajaChica && (
                      <div className="col-12">
                        <div className="alert alert-info py-2 mb-0">
                          <i className="bi bi-safe me-1"></i>
                          <strong>Caja Chica:</strong> Disponible {formatCurrency(cajaChica.monto_disponible)} de {formatCurrency(cajaChica.monto_asignado)}
                          {/* FIX: funds validation accounts for IVA total when aplica_iva is true */}
                          {gastoForm.monto && !isNaN(parseFloat(gastoForm.monto)) && (() => {
                            const montoEfectivo = gastoForm.aplica_iva ? parseFloat(gastoForm.monto) * 1.15 : parseFloat(gastoForm.monto);
                            return montoEfectivo > parseFloat(cajaChica.monto_disponible || 0);
                          })() && (
                            <div className="text-danger mt-1">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              <strong>Fondos insuficientes.</strong> El monto{gastoForm.aplica_iva ? ' (con IVA)' : ''} excede lo disponible en caja chica.
                            </div>
                          )}
                          <div className="text-muted mt-1 small">
                            <i className="bi bi-paperclip me-1"></i>Los gastos de caja chica requieren comprobante adjunto para poder pagarse.
                          </div>
                        </div>
                      </div>
                    )}
                    {!editGastoId && gastoForm.metodo_pago !== 'caja_chica' && (
                      <div className="col-12">
                        <div className="form-check">
                          <input type="checkbox" className="form-check-input" id="pagarDirecto"
                            checked={gastoForm.pagar_directo}
                            onChange={(e) => setGastoForm({ ...gastoForm, pagar_directo: e.target.checked })} />
                          <label className="form-check-label" htmlFor="pagarDirecto">
                            <i className="bi bi-lightning-charge text-warning me-1"></i>
                            <strong>Pagar directamente</strong> — Registrar y pagar en un solo paso
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowNuevoGasto(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-danger" onClick={handleCrearGasto} disabled={saving || (!editGastoId && adjuntosGasto.length === 0)}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando...</> :
                      editGastoId ? <><i className="bi bi-check-lg me-1"></i>Actualizar</> :
                        gastoForm.pagar_directo ? <><i className="bi bi-cash me-1"></i>Registrar y Pagar</> :
                          <><i className="bi bi-plus-lg me-1"></i>Registrar Gasto</>}
                    {!editGastoId && adjuntosGasto.length === 0 && <small className="ms-1 opacity-75">(requiere comprobante)</small>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* MODAL: Editar Categoría                                          */}
      {/* ================================================================ */}
      {showEditCategoria && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowEditCategoria(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-tags me-2"></i>{catForm.id ? 'Editar Categoría' : 'Nueva Categoría'}
                  </h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowEditCategoria(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Nombre *</label>
                    <input type="text" className="form-control" value={catForm.nombre}
                      onChange={(e) => setCatForm({ ...catForm, nombre: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Descripción</label>
                    <input type="text" className="form-control" value={catForm.descripcion}
                      onChange={(e) => setCatForm({ ...catForm, descripcion: e.target.value })} />
                  </div>
                  <div className="row g-3">
                    <div className="col-8">
                      <label className="form-label small fw-semibold">Icono (Bootstrap Icons)</label>
                      <input type="text" className="form-control" value={catForm.icono} placeholder="bi-tag"
                        onChange={(e) => setCatForm({ ...catForm, icono: e.target.value })} />
                      <div className="form-text">Ej: bi-lightning-charge, bi-truck, bi-tools</div>
                    </div>
                    <div className="col-4">
                      <label className="form-label small fw-semibold">Color</label>
                      <input type="color" className="form-control form-control-color w-100" value={catForm.color}
                        onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} />
                    </div>
                  </div>
                  {/* Preview */}
                  <div className="mt-3 p-3 bg-light rounded text-center">
                    <span className="badge fs-6" style={{ backgroundColor: catForm.color + '22', color: catForm.color }}>
                      <i className={`bi ${catForm.icono} me-1`}></i>{catForm.nombre || 'Vista previa'}
                    </span>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowEditCategoria(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-primary" onClick={handleGuardarCategoria} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando...</> :
                      <><i className="bi bi-check-lg me-1"></i>{catForm.id ? 'Actualizar' : 'Crear'}</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* MODAL: Abrir Caja Chica                                          */}
      {/* ================================================================ */}
      {showAbrirCC && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowAbrirCC(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-success text-white">
                  <h5 className="modal-title"><i className="bi bi-safe me-2"></i>Abrir Caja Chica</h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowAbrirCC(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Monto a Asignar *</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input type="number" className="form-control" step="0.01" min="1"
                        value={ccForm.monto_asignado}
                        onChange={(e) => setCcForm({ ...ccForm, monto_asignado: e.target.value })}
                        autoFocus />
                    </div>
                    <div className="form-text">Este será el fondo inicial de la caja chica</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Observaciones</label>
                    <textarea className="form-control" rows="2" value={ccForm.observaciones}
                      onChange={(e) => setCcForm({ ...ccForm, observaciones: e.target.value })} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowAbrirCC(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-success" onClick={handleAbrirCC} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Abriendo...</> :
                      <><i className="bi bi-unlock me-1"></i>Abrir Caja Chica</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* MODAL: Reponer Caja Chica                                        */}
      {/* ================================================================ */}
      {showReponerCC && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowReponerCC(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-warning text-dark">
                  <h5 className="modal-title"><i className="bi bi-arrow-clockwise me-2"></i>Reponer Caja Chica</h5>
                  <button className="btn-close" onClick={() => setShowReponerCC(false)} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-info py-2 small">
                    <i className="bi bi-info-circle me-1"></i>
                    Disponible actual: <strong>{formatCurrency(cajaChica?.monto_disponible || 0)}</strong> de {formatCurrency(cajaChica?.monto_asignado || 0)}
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Monto a Reponer *</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input type="number" className="form-control" step="0.01" min="1"
                        value={reponerForm.monto}
                        onChange={(e) => setReponerForm({ monto: e.target.value })}
                        autoFocus />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowReponerCC(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-warning" onClick={handleReponerCC} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Reponiendo...</> :
                      <><i className="bi bi-arrow-clockwise me-1"></i>Reponer</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* MODAL: Anular Gasto                                              */}
      {/* ================================================================ */}
      {showAnular && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowAnular(null)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title"><i className="bi bi-x-circle me-2"></i>Anular Gasto</h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowAnular(null)} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-warning py-2 small">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Si el gasto ya fue pagado, se revertirá el movimiento de caja.
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Motivo de anulación</label>
                    <textarea className="form-control" rows="2" value={anularMotivo}
                      onChange={(e) => setAnularMotivo(e.target.value)}
                      placeholder="Ingrese el motivo..." autoFocus />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowAnular(null)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-danger" onClick={handleAnular} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Anulando...</> :
                      <><i className="bi bi-x-circle me-1"></i>Anular Gasto</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* MODAL: Detalle Gasto + Comprobantes                              */}
      {/* ================================================================ */}
      {showDetalleGasto && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowDetalleGasto(null)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-receipt me-2"></i>Gasto: {showDetalleGasto.codigo}
                  </h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowDetalleGasto(null)} />
                </div>
                <div className="modal-body">
                  {/* FIX: null checks for all detail modal fields */}
                  {/* Info del gasto */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <p className="mb-1"><strong>Concepto:</strong> {showDetalleGasto.concepto || '—'}</p>
                      <p className="mb-1"><strong>Categoría:</strong> {showDetalleGasto.categoria_nombre || '—'}</p>
                      <p className="mb-1"><strong>Proveedor:</strong> {showDetalleGasto.proveedor_nombre || '—'}</p>
                      <p className="mb-1"><strong>Método:</strong> {showDetalleGasto.metodo_pago || '—'}</p>
                    </div>
                    <div className="col-md-6 text-md-end">
                      {showDetalleGasto.aplica_iva ? (
                        <>
                          <p className="mb-1"><strong>Subtotal:</strong> {formatCurrency(showDetalleGasto.monto)}</p>
                          <p className="mb-1"><strong>IVA ({showDetalleGasto.iva_porcentaje || 15}%):</strong> {formatCurrency(showDetalleGasto.monto_iva || 0)}</p>
                          {/* FIX: safe pattern for total_con_iva - use monto as fallback when total_con_iva is 0/null */}
                          <p className="mb-1"><strong>Total:</strong> <span className="text-danger fw-bold fs-5">{formatCurrency(parseFloat(showDetalleGasto.total_con_iva) > 0 ? showDetalleGasto.total_con_iva : showDetalleGasto.monto)}</span></p>
                        </>
                      ) : (
                        // FIX: safe pattern for total_con_iva display
                        <p className="mb-1"><strong>Monto:</strong> <span className="text-danger fw-bold fs-5">{formatCurrency(parseFloat(showDetalleGasto.total_con_iva) > 0 ? showDetalleGasto.total_con_iva : showDetalleGasto.monto)}</span></p>
                      )}
                      {/* FIX: null checks for estado and created_at */}
                      <p className="mb-1"><strong>Estado:</strong> <span className={`badge ${estadoBadge(showDetalleGasto.estado)}`}>{showDetalleGasto.estado || '—'}</span></p>
                      <p className="mb-1"><strong>Fecha:</strong> {showDetalleGasto.created_at ? new Date(showDetalleGasto.created_at).toLocaleDateString('es-EC') : '—'}</p>
                      {showDetalleGasto.es_caja_chica && (
                        <span className="badge bg-warning text-dark"><i className="bi bi-safe me-1"></i>Caja Chica</span>
                      )}
                    </div>
                  </div>

                  {showDetalleGasto.notas && (
                    <div className="alert alert-secondary py-2 small mb-3">
                      <strong>Notas:</strong> {showDetalleGasto.notas}
                    </div>
                  )}

                  {/* ─── Sección Comprobantes ─── */}
                  <hr />
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                      <i className="bi bi-paperclip me-2"></i>Comprobantes
                      {showDetalleGasto.es_caja_chica && <span className="badge bg-danger ms-2">Obligatorio</span>}
                    </h6>
                    {showDetalleGasto.estado !== 'anulado' && !modoEspectador && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="d-none"
                          multiple
                          accept="image/*,application/pdf"
                          onChange={(e) => { if (e.target.files.length > 0) handleUploadComprobantes(e.target.files); }}
                        />
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingComp || comprobantes.length >= 6}
                        >
                          {uploadingComp ? (
                            <><span className="spinner-border spinner-border-sm me-1"></span>Subiendo...</>
                          ) : (
                            <><i className="bi bi-upload me-1"></i>Subir Comprobante</>
                          )}
                        </button>
                      </>
                    )}
                  </div>

                  {comprobantes.length >= 6 && (
                    <div className="alert alert-warning py-2 small mb-2">
                      <i className="bi bi-info-circle me-1"></i>Límite máximo de 6 comprobantes alcanzado.
                    </div>
                  )}

                  {comprobantes.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-file-earmark-x fs-1 d-block mb-2"></i>
                      <p className="mb-0">Sin comprobantes adjuntos</p>
                      {showDetalleGasto.es_caja_chica && (
                        <small className="text-danger">⚠ Se requiere al menos un comprobante para pagar este gasto de caja chica</small>
                      )}
                    </div>
                  ) : (
                    <div className="row g-2">
                      {comprobantes.map(comp => (
                        <div key={comp.id} className="col-md-6">
                          <div className="card border">
                            <div className="card-body py-2 px-3 d-flex align-items-center gap-2">
                              {/* FIX: safe optional chaining on archivo_tipo for null check */}
                              {comp.archivo_tipo?.startsWith('image') ? (
                                <img
                                  src={getImageUrl(comp.archivo_url)}
                                  alt={comp.archivo_nombre}
                                  style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6 }}
                                />
                              ) : (
                                <div className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 rounded"
                                  style={{ width: 50, height: 50 }}>
                                  <i className="bi bi-file-pdf text-danger fs-4"></i>
                                </div>
                              )}
                              <div className="flex-grow-1 overflow-hidden">
                                {/* FIX: null checks for archivo fields */}
                                <div className="small fw-semibold text-truncate">{comp.archivo_nombre || 'Sin nombre'}</div>
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                  {comp.archivo_size ? `${(comp.archivo_size / 1024).toFixed(0)} KB` : ''}
                                  {comp.subido_por_nombre && ` · ${comp.subido_por_nombre}`}
                                </div>
                              </div>
                              <div className="d-flex gap-1">
                                <a
                                  href={getImageUrl(comp.archivo_url)}
                                  target="_blank" rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                  title="Ver"
                                >
                                  <i className="bi bi-eye"></i>
                                </a>
                                {['pendiente', 'aprobado'].includes(showDetalleGasto.estado) && !modoEspectador && (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    title="Eliminar"
                                    onClick={() => handleDeleteComprobante(comp.id)}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowDetalleGasto(null)}>Cerrar</button>
                  {showDetalleGasto.estado === 'pendiente' && !modoEspectador && (
                    <>
                      <button className="btn btn-info" onClick={() => { handleAprobar(showDetalleGasto.id); setShowDetalleGasto(null); }}>
                        <i className="bi bi-check-lg me-1"></i>Aprobar
                      </button>
                      <button className="btn btn-success" onClick={() => { handlePagar(showDetalleGasto.id); setShowDetalleGasto(null); }}>
                        <i className="bi bi-cash me-1"></i>Pagar
                      </button>
                    </>
                  )}
                  {showDetalleGasto.estado === 'aprobado' && !modoEspectador && (
                    <button className="btn btn-success" onClick={() => { handlePagar(showDetalleGasto.id); setShowDetalleGasto(null); }}>
                      <i className="bi bi-cash me-1"></i>Pagar
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Gastos;
// build 1773861338
